import { z } from "zod";

export const HeroClusterStatusSchema = z.enum(["OPEN", "REJECTED", "RESOLVED"]);
export type HeroClusterStatus = z.infer<typeof HeroClusterStatusSchema>;
export const HeroClusterStatus = HeroClusterStatusSchema.Enum;

export const HeroFindingConfidenceSchema = z.enum(["LOW", "MEDIUM", "HIGH"]);
export type HeroFindingConfidence = z.infer<typeof HeroFindingConfidenceSchema>;
export const HeroFindingConfidence = HeroFindingConfidenceSchema.Enum;

export const HeroFindingTypeSchema = z.enum([
  "HERO_ANALYZER",
  "RETRIEVAL_FAILURE_CLASSIFIER",
]);
export type HeroFindingType = z.infer<typeof HeroFindingTypeSchema>;
export const HeroFindingType = HeroFindingTypeSchema.Enum;

/**
 * Analysis-phase producers the scheduled pipeline can dispatch. Add a
 * value here when introducing a new analyzer or classifier (for example
 * a future retrieval-failure root-cause analyzer).
 */
export const HeroPipelineProducerSchema = z.enum([
  "HERO_ANALYZER",
  "RETRIEVAL_FAILURE_CLASSIFIER",
]);
export type HeroPipelineProducer = z.infer<typeof HeroPipelineProducerSchema>;
export const HeroPipelineProducer = HeroPipelineProducerSchema.Enum;

export const RetrievalFailureClassifierPayloadSchema = z.object({
  findingType: z.literal(HeroFindingType.RETRIEVAL_FAILURE_CLASSIFIER),
  version: z.literal(1),
  failureMode: z.string(),
  candidate: z.string().nullable(),
});
export type RetrievalFailureClassifierPayload = z.infer<
  typeof RetrievalFailureClassifierPayloadSchema
>;

export const HeroFindingPayloadSchema = z.discriminatedUnion("findingType", [
  RetrievalFailureClassifierPayloadSchema,
]);
export type HeroFindingPayload = z.infer<typeof HeroFindingPayloadSchema>;

/**
 * Config for the scheduled Hero pipeline (analysis followed by
 * aggregation), delivered via the `hero-pipeline-config` LaunchDarkly
 * flag (per-environment). `enabled` is the master switch; `enabledProducers`
 * selects which analysis-phase analyzers/classifiers to dispatch;
 * `aggregationEnabled` and `enabledAggregators` do the same for aggregation.
 */
export const HeroPipelineConfigSchema = z.object({
  /** Master switch for the whole pipeline (both phases). */
  enabled: z.boolean(),
  /**
   * How long after analysis prep completes that the analysis orchestrator
   * may keep dispatching new analyzer runs. In-flight runs are allowed to finish
   * past the deadline (up to the analyzer workflow timeout).
   * Aggregation runs after analysis settles regardless.
   */
  analysisDispatchDeadlineMinutes: z.number(),
  maxConcurrentAnalyzers: z.number(),
  /** How far back from pipeline start to look for eligible threads. */
  eligibleThreadsForAnalysisLookbackHours: z.number(),
  /**
   * Max eligible threads fetched per analysis orchestrator run, i.e.
   * across all analyzer agents dispatched over the course of
   * one analysis orchestrator run.
   */
  eligibleThreadsForAnalysisLimit: z.number(),
  /** Master switch for the aggregation phase; analysis can run without it. */
  aggregationEnabled: z.boolean(),
  /**
   * Which finding types to aggregate when `aggregationEnabled` is true.
   * Each value is a producer / `HeroFindingType`. Empty means no aggregator
   * children are started.
   */
  enabledAggregators: z.array(HeroFindingTypeSchema),
  /**
   * Aggregator concurrency defaults to 1 (sequential execution):
   * each aggregator snapshots existing clusters at setup,
   * so concurrent aggregators would mint duplicate clusters.
   */
  maxConcurrentAggregators: z.number(),
  /**
   * How far back to look for unclustered findings to aggregate. Covers the
   * current run's fresh findings plus stragglers from failed or skipped
   * prior runs.
   */
  unclusteredFindingLookbackHours: z.number(),
  /**
   * Max unclustered findings fetched per findingType per aggregation
   * orchestrator run — the nightly processing cap for one type.
   */
  unclusteredFindingLimit: z.number(),
  /** Max unclustered findings passed to a single Hero aggregator agent run. */
  aggregatorChunkSize: z.number(),
  /**
   * Max existing clusters loaded into a single Hero aggregator agent run's
   * context (bounds prompt size vs. clustering recall against prior clusters).
   */
  maxExistingClustersPerAggregatorAgentRun: z.number(),
  /** Min findings required to promote a new Hero cluster. */
  minFindingsPerNewCluster: z.number(),
  /** Min distinct source threads required to promote a new Hero cluster. */
  minDistinctThreadsPerNewCluster: z.number(),
  /**
   * Which analysis-phase producers to dispatch when `enabled` is true.
   * Empty means the pipeline can still run aggregation without starting
   * any analyzer or classifier.
   */
  enabledProducers: z.array(HeroPipelineProducerSchema),
  /** Max eligible source threads fetched per classifier dispatch run. */
  retrievalClassifierThreadLimit: z.number(),
  /**
   * Threads with more than this many messages are skipped by the classifier
   * rather than truncated: a partial transcript risks false-positive findings.
   */
  retrievalClassifierMaxThreadMessages: z.number(),
});
export type HeroPipelineConfig = z.infer<typeof HeroPipelineConfigSchema>;

export const defaultHeroPipelineConfig: HeroPipelineConfig = {
  analysisDispatchDeadlineMinutes: 300,
  enabled: false,
  maxConcurrentAnalyzers: 5,
  eligibleThreadsForAnalysisLookbackHours: 24,
  /**
   * Conservative default so accidental enables won't
   * initiate an excessive number of analyzer agents.
   */
  eligibleThreadsForAnalysisLimit: 10,
  aggregationEnabled: false,
  enabledAggregators: [],
  maxConcurrentAggregators: 1,
  unclusteredFindingLookbackHours: 7 * 24,
  unclusteredFindingLimit: 250,
  aggregatorChunkSize: 50,
  maxExistingClustersPerAggregatorAgentRun: 50,
  minFindingsPerNewCluster: 3,
  minDistinctThreadsPerNewCluster: 3,
  enabledProducers: [],
  retrievalClassifierThreadLimit: 100,
  retrievalClassifierMaxThreadMessages: 300,
};
